export interface Theme {
  logo: string;
  client: string;
  border: string;
  bg: string;
  'bg-default': string;
  'home-subheading': string;
  'home-heading-text': string;
  'home-button-text': string;
  'gradient-home-button-text': string;
  'home-button-border': string;
  'button-bg': string;
  'action-button-bg': string;
  'text-600': string;
  'text-default-800': string;
  color: string;
  'star-stopColor': string;
  'star-stopOffsetColor': string;
  p_color: string;
  text_sub_heading?: string;
  s_color: string;
  gradient: string;
  sandraMessage: string;
  allowedPaths: string[];
  onlyPaymentsPathAllowed: boolean;
  hideSidebarPermanently: boolean;
  showSsnField: boolean;
  showDobField: boolean;
  showGarantorNumberField: boolean;
  showGarantorLastNameField: boolean;
  /**
   * Optional go-live notice shown on the patient login screen.
   *
   * <p>Per-tenant and optional on purpose. The copy names the practice ("our office") and carries a
   * real phone number, so hardcoding it anywhere shared would show St. Luke's number to Axis, Ovum and
   * PatientBud patients. A tenant without these keys renders nothing.
   */
  transitionNoticeHeading?: string;
  /** Body paragraphs, rendered in order. */
  transitionNotice?: string[];
  /** Displayed under the notice, and used as the tel: link target. */
  supportPhone?: string;

  /**
   * Tenant-scoped copy for the guarantor lookup/signup field.
   *
   * These exist because the field is SHARED: showGarantorNumberField is true for `default`, `stluke`
   * and `axis`, and LinkPayment carries no tenant gate at all. St. Luke is mid Meditech->Cerner
   * migration and its patients hold either a Meditech guarantor number or a Cerner guarantor CMRN, so
   * its copy has to name both -- but naming "CMRN" for Axis or the default tenant would put another
   * EHR's vocabulary in front of their patients. Absent keys fall back to the shared wording.
   */
  guarantorFieldLabel?: string;
  /** Placeholder for the same field. Keep any example identifier consistent with the tenant's EHR. */
  guarantorFieldPlaceholder?: string;
  /** Lookup-modal intro sentence, when the tenant needs to name more than one identifier. */
  guarantorFieldIntro?: string;
  /**
   * Warn when a value is shaped like a Cerner encounter FIN rather than a guarantor CMRN. Only
   * meaningful for a tenant whose patients can hold a FIN at all, so it is opt-in per tenant.
   */
  showEncounterFinHint?: boolean;

}
export const THEMES: Record<string, Theme> = {
  default: {
    logo: 'PatientBud',
    client: "patientbud",
    text_sub_heading:
      'PatientBud simplifies healthcare billing—get clear, reliable answers in seconds.',
    color: 'teal',
    bg: '#0699A1',
    p_color: '#08B4BD',
    s_color: '#242E49',
    border: 'border-teal-300',
    'star-stopColor': '#42CBD2',
    'star-stopOffsetColor': '#0699A1',
    'bg-default': 'bg-[#F2F5F9]',
    'home-subheading': 'text-teal-40',
    'home-heading-text': 'text-gray-70',
    'button-bg': 'bg-teal-40',
    'home-button-border': 'border-teal-40',
    'home-button-text': 'text-teal-40',
    'gradient-home-button-text': 'from-amber-600 to-amber-400',
    'action-button-bg': 'bg-teal-50 hover:bg-teal-100',
    'text-600': 'text-teal-600',
    'text-default-800': 'text-gray-800',
    gradient: 'bg-teal-gradient',
    sandraMessage: "Hello! I'm Sandra, your AI assistant for medical billing and insurance queries. How can I help you today?",
    allowedPaths: ['/payments'],
    onlyPaymentsPathAllowed: false,
    hideSidebarPermanently: true,
    showSsnField: true,
    showDobField: true,
    showGarantorNumberField: true,
    showGarantorLastNameField: true,
  },
  ovum: {
    logo: 'Ovum',
    client: "ovum",
    text_sub_heading:
      'Hi, I am Sandra at Ovum! Ask me any insurance, offers, pricing, estimate or bill related question!',
    color: 'orange',
    bg: '#351C48',
    p_color: '#502C3B',
    s_color: '#5B4074',
    border: 'border-orange-300',
    'star-stopColor': '#8D5B6F',
    'star-stopOffsetColor': '#351C48',
    'bg-default': 'bg-[#F8F9FC]',
    'home-subheading': 'text-[#502C3B]',
    'button-bg': 'bg-orange',
    'home-button-text': 'text-orange',
    'home-button-border': 'border-orange',
    'home-heading-text': 'text-gray-900',
    'gradient-home-button-text': 'from-purple-600 to-blue-400',
    'action-button-bg': 'bg-orange',
    'text-600': 'text-[#502C3B]',
    'text-default-800': 'text-gray-800',
    gradient: 'bg-orange-gradient',
    sandraMessage: "Hello! I'm Sandra, your Ovum AI assistant. How can I help you today?",
    allowedPaths: ['/'],
    onlyPaymentsPathAllowed: false,
    hideSidebarPermanently: true,
    showSsnField: false,
    showDobField: false,
    showGarantorNumberField: false,
    showGarantorLastNameField: false,
  },
  abchospital: {
    logo: 'AbcHospital',
    client: "abchospital",
    text_sub_heading:
      'Abc Hospital simplifies healthcare billing—get clear, reliable answers in seconds.',
    color: 'teal',
    bg: '#0699A1',
    p_color: '#08B4BD',
    s_color: '#242E49',
    border: 'border-teal-300',
    'star-stopColor': '#42CBD2',
    'star-stopOffsetColor': '#0699A1',
    'bg-default': 'bg-[#F2F5F9]',
    'home-subheading': 'text-teal-40',
    'home-heading-text': 'text-gray-70',
    'button-bg': 'bg-teal-40',
    'home-button-border': 'border-teal-40',
    'home-button-text': 'text-teal-40',
    'gradient-home-button-text': 'from-purple-600 to-blue-400',
    'action-button-bg': 'bg-teal-50 hover:bg-teal-100',
    'text-600': 'text-teal-600',
    'text-default-800': 'text-gray-800',
    gradient: 'bg-teal-gradient',
    sandraMessage: "Hello! I'm Sandra, your AI assistant for medical billing and insurance queries. How can I help you today?",
    allowedPaths: ['/payments'],
    onlyPaymentsPathAllowed: false,
    hideSidebarPermanently: false,
    showSsnField: false,
    showDobField: false,
    showGarantorNumberField: false,
    showGarantorLastNameField: false,
  },
  stluke: {
    logo: 'StLuke',
    client: "stluke",
    text_sub_heading: '',
    // St. Luke palette (from logo SVG): primary maroon + soft warm neutrals
    color: 'maroon',
    bg: '#F2F5F9',
    p_color: '#71001F',
    s_color: '#242E49',
    border: 'border-[#71001F]',
    'star-stopColor': '#71001F',
    'star-stopOffsetColor': '#9B1236',
    'bg-default': 'bg-[#F2F5F9]',
    'home-subheading': 'text-[#242E49]',
    'home-heading-text': 'text-[#242E49]',
    'button-bg': 'bg-[#71001F]',
    'home-button-border': 'border-[#71001F]',
    'home-button-text': 'text-[#71001F]',
    'gradient-home-button-text': 'from-[#71001F] to-[#9B1236]',
    'action-button-bg': 'bg-[#F4F5D7] hover:bg-[#EEEFCB]',
    'text-600': 'text-[#71001F]',
    'text-default-800': 'text-[#242E49]',
    gradient: 'bg-gradient-to-r from-[#71001F] to-[#9B1236]',
    sandraMessage: "Hello! I'm Sandra, your AI assistant for medical billing and insurance queries. How can I help you today?",
    allowedPaths: ['/payments'],
    onlyPaymentsPathAllowed: true,
    hideSidebarPermanently: true,
    showSsnField: true,
    showDobField: true,
    showGarantorNumberField: true,
    showGarantorLastNameField: true,
    transitionNoticeHeading: "We're pleased to introduce our new system!",
    transitionNotice: [
      'As we transition to this new system, you may experience occasional issues while we work to ensure everything operates smoothly. We appreciate your patience and understanding during this process.',
      "If you have any questions, concerns, or encounter any difficulties with your payment, please don't hesitate to contact our office. Our team is happy to assist and is committed to making this transition as seamless as possible.",
      'Thank you for your continued support and understanding.',
    ],
    // Matches ContactBanner and the Notification-service patient emails, which both use this format.
    // The notice previously carried '406.676.4441' while ContactBanner rendered '(406) 676-4441', so
    // the same number appeared in two formats in one product.
    supportPhone: '(406) 676-4441',
    // Dual-EHR period: a St. Luke patient holds a Meditech guarantor number OR a Cerner guarantor
    // CMRN, and the lookup accepts either (it routes on digit shape). The field has to say so.
    guarantorFieldLabel: 'Guarantor Number or CMRN',
    // Both example shapes, so either kind of patient recognises their own number. The CMRN example
    // is deliberately a round, obviously-fabricated number: an earlier draft used a value that was a
    // truncation of one in the sample statement extract, which is not something example copy should
    // ever be derived from. Pinned by themes.notice.test.ts.
    guarantorFieldPlaceholder: 'From your statement, e.g. GN00000123 or 6200000',
    guarantorFieldIntro:
      'Please enter the guarantor number or CMRN from your statement, along with your last name, to access your payment details.',
    showEncounterFinHint: true,
  },
  axis: {
    logo: 'Axis',
    client: "axis",
    text_sub_heading:
      '',
    color: 'teal',
    bg: '#0699A1',
    p_color: '#08B4BD',
    s_color: '#242E49',
    border: 'border-teal-300',
    'star-stopColor': '#42CBD2',
    'star-stopOffsetColor': '#0699A1',
    'bg-default': 'bg-[#F2F5F9]',
    'home-subheading': 'text-teal-40',
    'home-heading-text': 'text-gray-70',
    'button-bg': 'bg-teal-40',
    'home-button-border': 'border-teal-40',
    'home-button-text': 'text-teal-40',
    'gradient-home-button-text': 'from-amber-600 to-amber-400',
    'action-button-bg': 'bg-teal-50 hover:bg-teal-100',
    'text-600': 'text-teal-600',
    'text-default-800': 'text-gray-800',
    gradient: 'bg-teal-gradient',
    sandraMessage: "Hello! I'm Sandra, your AI assistant for medical billing and insurance queries. How can I help you today?",
    allowedPaths: ['/payments'],
    onlyPaymentsPathAllowed: false,
    hideSidebarPermanently: true,
    showSsnField: true,
    showDobField: true,
    showGarantorNumberField: true,
    showGarantorLastNameField: true,
  }
};
